/**
 * Subscription Types (TASK-061)
 *
 * Type definitions for subscription management UI.
 */

export interface SubscriptionPlan {
  id: string;
  name: string;
  plan_type: string;
  description: string;
  price_monthly_usd: string;
  price_yearly_usd: string;
  max_users: number;
  max_estimates_per_month: number;
  max_data_sources: number;
  storage_limit_mb?: number;
  /** KAN-988: коды функций тарифа (lib/features.ts), отсортированы. */
  features: string[];
  is_active?: boolean;
  /** KAN-989: абонентка в рублях, если сервер её отдаёт ("0.00" у бесплатного). */
  price_monthly_rub?: string;
  /** KAN-989: false у служебных тарифов (Партнёр), если сервер отдаёт поле. */
  is_public?: boolean;
}

export interface OrganizationSubscription {
  id: string;
  plan: SubscriptionPlan;
  billing_period: string;
  status: string;
  trial_ends_at?: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  auto_renew: boolean;
  cancel_at_period_end: boolean;
  /**
   * KAN-341: право работать приходит ГОТОВЫМ с бэкенда. Выводить его здесь из
   * статуса и дат нельзя: то же правило применяет гейт, и вторая реализация
   * однажды покажет «Активна» там, где запросы уже отклоняются.
   */
  has_access: boolean;
  /** Код причины отказа: tariff_missing | tariff_expired | tariff_canceled. */
  access_reason: string;
  /** Готовый текст отказа для показа человеку. Пусто, если доступ есть. */
  access_message: string;
  /** Полных суток до конца срока. null — срок не задан. */
  days_left: number | null;
  /** До какого числа тариф действует. У пробы — ближайший из двух сроков. */
  ends_at: string | null;
  /**
   * KAN-989: при PAST_DUE дата, когда выключатся функции тарифа. Считает
   * сервер, кабинет срок грейса сам не прибавляет. null вне просрочки.
   */
  grace_ends_at?: string | null;
}

export interface SubscriptionUsage {
  estimates_used: number;
  estimates_limit: number;
  users_count: number;
  max_users: number;
  data_sources_count: number;
  max_data_sources: number;
  storage_used_mb: number;
  storage_limit_mb: number;
}

export interface CreatePaymentPayload {
  plan_id: string;
  billing_period?: string;
}

export interface CreatePaymentResponse {
  confirmation_url: string;
  payment_id: string;
}

export type CancelSubscriptionResponse = OrganizationSubscription;

/**
 * Format subscription status for display
 */
export function formatSubscriptionStatus(status: string): string {
  const statusMap: Record<string, string> = {
    TRIAL: 'Пробный период',
    ACTIVE: 'Активна',
    PAST_DUE: 'Просрочена',
    CANCELED: 'Отменена',
    EXPIRED: 'Истекла',
  };
  return statusMap[status] || status;
}

/**
 * Format billing period for display
 */
export function formatBillingPeriod(period: string): string {
  const periodMap: Record<string, string> = {
    MONTHLY: 'Ежемесячно',
    YEARLY: 'Ежегодно',
  };
  return periodMap[period] || period;
}
